import { inRange } from 'lodash-es';
import { ownershipService } from '../../../services';
import { assertUnreachable } from '../../../utils/assertUnreachable';
import type { ValidationResult as EditableInputValidationResult } from '../../EditableInput/types';
import {
  MEETING_ID_ALLOWED,
  MEETING_ID_MAX_LENGTH,
  MEETING_ID_MIN_LENGTH,
  MEETING_TITLE_MAX_LENGTH,
  MEETING_TITLE_MIN_LENGTH,
  PASSCODE_ALLOWED,
  PASSCODE_MAX_LENGTH,
  PASSCODE_MIN_LENGTH,
} from './constants';

export const isMeetingIdValid = (value: string): boolean => {
  return (
    inRange(value.length, MEETING_ID_MIN_LENGTH, MEETING_ID_MAX_LENGTH + 1) &&
    MEETING_ID_ALLOWED.test(value)
  );
};

export const isPasscodeValid = (value: string): boolean => {
  if (value === '') {
    return true;
  }
  return PASSCODE_ALLOWED.test(value) && value.length >= PASSCODE_MIN_LENGTH;
};

/**
 * Validate email format using regex
 */
export const isValidEmail = (value: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

/**
 * Validate Meeting Title with detailed error messages
 */
export const validateMeetingTitle = (value: string): { isValid: boolean; error?: string } => {
  // Check for invalid characters (e.g. special characters) first so user gets this error before length
  if (!value || value.length < MEETING_TITLE_MIN_LENGTH) {
    return {
      isValid: false,
      error:
        MEETING_TITLE_MIN_LENGTH === 1
          ? 'Meeting Title cannot be empty'
          : `Meeting Title must be at least ${MEETING_TITLE_MIN_LENGTH} characters`,
    };
  }

  if (value.length > MEETING_TITLE_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Meeting Title must be no more than ${MEETING_TITLE_MAX_LENGTH} characters`,
    };
  }

  return { isValid: true };
};

/**
 * Validate Meeting ID format with detailed error messages
 */
export const validateMeetingIdFormat = (value: string): { isValid: boolean; error?: string } => {
  // Check for invalid characters (e.g. special characters) first so user gets this error before length
  if (value && !MEETING_ID_ALLOWED.test(value)) {
    return {
      isValid: false,
      error: 'Meeting ID must contain only letters and numbers',
    };
  }

  if (!value || value.length < MEETING_ID_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Meeting ID must be at least ${MEETING_ID_MIN_LENGTH} characters`,
    };
  }

  if (value.length > MEETING_ID_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Meeting ID must be no more than ${MEETING_ID_MAX_LENGTH} characters`,
    };
  }

  return { isValid: true };
};

/**
 * Validate passcode with detailed error messages
 */
export const validatePasscode = (value: string): { isValid: boolean; error?: string } => {
  if (value === '') {
    return { isValid: true };
  }

  if (!PASSCODE_ALLOWED.test(value)) {
    return {
      isValid: false,
      error: 'Passcode must contain only digits',
    };
  }

  if (value.length < PASSCODE_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Passcode must be at least ${PASSCODE_MIN_LENGTH} digits`,
    };
  }

  if (value.length > PASSCODE_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Passcode must be no more than ${PASSCODE_MAX_LENGTH} digits`,
    };
  }

  return { isValid: true };
};

/**
 * Check if meeting ID is available via API
 */
export const checkMeetingIdAvailability = async (
  meetingId: string,
  currentRoom: string,
): Promise<EditableInputValidationResult> => {
  // First check format
  const formatValidation = validateMeetingIdFormat(meetingId);
  if (!formatValidation.isValid) {
    return formatValidation;
  }

  if (!meetingId || meetingId === currentRoom) {
    return { isValid: true };
  }

  try {
    const availability = await ownershipService.getScheduleAvailability(meetingId);

    switch (availability) {
      case 'RoomIsFree':
        return { isValid: true };
      case 'RoomIsInUse':
        return { isValid: false, error: 'Meeting ID already in use' };
      case 'RoomIsInvalid':
        return { isValid: false, error: 'Meeting ID is invalid' };
      default:
        return assertUnreachable(availability);
    }
  } catch (error: unknown) {
    console.error('[validation] Error checking availability:', error);
    return { isValid: false, error: 'Unable to verify availability' };
  }
};
